// TTF/OTF -> three.js Font conversion (adapted from three's TTFLoader, which
// imports opentype.js from a CDN and so cannot be used under COEP).

import opentype from 'opentype.js';
import { Font, type FontData } from 'three/examples/jsm/loaders/FontLoader.js';

export interface FontInfo {
  id: string;
  name: string;
  url: string;
}

/** Bundled OFL fonts with Latin + Cyrillic coverage (see public/fonts). */
export const BUNDLED_FONTS: FontInfo[] = [
  { id: 'russo', name: 'Russo One', url: '/fonts/RussoOne-Regular.ttf' },
  { id: 'ptserif', name: 'PT Serif Bold', url: '/fonts/PT_Serif-Web-Bold.ttf' },
  { id: 'lobster', name: 'Lobster', url: '/fonts/Lobster-Regular.ttf' },
];

const cache = new Map<string, Promise<Font>>();

export function parseFont(buffer: ArrayBuffer): Font {
  return new Font(convert(opentype.parse(buffer)) as unknown as FontData);
}

export function loadFont(id: string): Promise<Font> {
  let p = cache.get(id);
  if (!p) {
    const info = BUNDLED_FONTS.find((f) => f.id === id) ?? BUNDLED_FONTS[0];
    p = fetch(info.url)
      .then((r) => {
        if (!r.ok) throw new Error(`font ${info.url}: HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(parseFont);
    p.catch(() => cache.delete(id));
    cache.set(id, p);
  }
  return p;
}

/** Registers a user-uploaded font under `custom:<name>`. */
export function registerFont(id: string, buffer: ArrayBuffer): Font {
  const font = parseFont(buffer);
  cache.set(id, Promise.resolve(font));
  return font;
}

function convert(font: opentype.Font) {
  const round = Math.round;
  const glyphs: Record<string, unknown> = {};
  const scale = 100000 / ((font.unitsPerEm || 2048) * 72);
  const all = font.glyphs as unknown as { length: number; glyphs: Record<number, opentype.Glyph> };
  for (let i = 0; i < all.length; i++) {
    const glyph = all.glyphs[i];
    const unicodes: number[] = glyph.unicodes?.length
      ? glyph.unicodes
      : glyph.unicode !== undefined
        ? [glyph.unicode]
        : [];
    if (!unicodes.length) continue;
    const token = {
      ha: round((glyph.advanceWidth ?? 0) * scale),
      x_min: round((glyph.xMin ?? 0) * scale),
      x_max: round((glyph.xMax ?? 0) * scale),
      o: '',
    };
    for (const c of glyph.path.commands as Array<Record<string, number | string>>) {
      const type = (c.type as string).toLowerCase() === 'c' ? 'b' : (c.type as string).toLowerCase();
      token.o += type + ' ';
      for (const [kx, ky] of [
        ['x', 'y'],
        ['x1', 'y1'],
        ['x2', 'y2'],
      ]) {
        if (c[kx] !== undefined && c[ky] !== undefined) {
          token.o += round((c[kx] as number) * scale) + ' ' + round((c[ky] as number) * scale) + ' ';
        }
      }
    }
    for (const u of unicodes) glyphs[String.fromCodePoint(u)] = token;
  }
  const head = font.tables.head as unknown as { xMin: number; xMax: number; yMin: number; yMax: number };
  const post = font.tables.post as unknown as { underlinePosition: number; underlineThickness: number };
  return {
    glyphs,
    familyName: font.getEnglishName('fontFamily'),
    ascender: round(font.ascender * scale),
    descender: round(font.descender * scale),
    underlinePosition: round(post.underlinePosition * scale),
    underlineThickness: round(post.underlineThickness * scale),
    boundingBox: {
      xMin: round(head.xMin * scale),
      xMax: round(head.xMax * scale),
      yMin: round(head.yMin * scale),
      yMax: round(head.yMax * scale),
    },
    resolution: 1000,
  };
}
